import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion } from '../store/gameStore';
import { useOptimizedCalculations } from '../store/optimizedGameStore';

interface CompanionSlotMachineProps {
  isFreePremiumSpin?: boolean;
  onFreePremiumSpinComplete?: () => void;
}

const CompanionSlotMachine: React.FC<CompanionSlotMachineProps> = ({ 
  isFreePremiumSpin = false, 
  onFreePremiumSpinComplete 
}) => {
  const { gems, spendGems, addGems, addCompanionToCollection, formatNumber } = useGameStore();
  const { setActiveCompanionOptimized } = useOptimizedCalculations();
  const [isSpinning, setIsSpinning] = useState(false);
  const [reels, setReels] = useState(['🎰', '🎰', '🎰']);
  const [result, setResult] = useState<{ 
    type: 'legendary' | 'epic' | 'rare' | 'common' | 'refund' | 'nothing'; 
    companion?: Companion;
    refundAmount?: number;
  } | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [freePremiumSpinUsed, setFreePremiumSpinUsed] = useState(false);
  const [autoSpinActive, setAutoSpinActive] = useState(false);
  const [autoSpinType, setAutoSpinType] = useState<'normal' | 'premium'>('normal');
  const [autoSpinCount, setAutoSpinCount] = useState(0);
  const [maxAutoSpins, setMaxAutoSpins] = useState(10);
  const [isUnlimitedAutoSpin, setIsUnlimitedAutoSpin] = useState(false); // Unbegrenzter Auto-Spin
  const [showDetails, setShowDetails] = useState(true);

  const SPIN_COST = 100; // 100 Diamanten pro Spin
  const PREMIUM_SPIN_COST = Math.max(10000, Math.floor(gems * 0.1)); // 10% aller Diamanten, mindestens 10.000

  // Auto-Spin Logik
  useEffect(() => {
    if (autoSpinActive && !isSpinning && !showResult) {
      // Bei unbegrenztem Auto-Spin nicht stoppen wegen Anzahl
      if (!isUnlimitedAutoSpin && autoSpinCount >= maxAutoSpins) {
        setAutoSpinActive(false);
        setAutoSpinCount(0);
        return;
      }

      const canAfford = autoSpinType === 'normal' 
        ? gems >= SPIN_COST 
        : gems >= PREMIUM_SPIN_COST;

      if (!canAfford) {
        setAutoSpinActive(false);
        setAutoSpinCount(0);
        return;
      }

      const timer = setTimeout(() => {
        if (autoSpinType === 'normal') {
          spinReels();
        } else {
          premiumSpin();
        }
        setAutoSpinCount(prev => prev + 1);
      }, 1000); // 1 Sekunde Pause zwischen Auto-Spins

      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSpinActive, isSpinning, showResult, autoSpinCount, maxAutoSpins, autoSpinType, gems, isUnlimitedAutoSpin, PREMIUM_SPIN_COST]);

  // Auto-Spin Ergebnis automatisch verarbeiten
  useEffect(() => {
    if (showResult && autoSpinActive && result) {
      // Bei Auto-Spin nur kurz anzeigen und dann automatisch einsammeln
      const timer = setTimeout(() => {
        handleClaimPrize();
      }, 800); // Nur 0.8 Sekunden kurz anzeigen bei Auto-Spin
      
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResult, autoSpinActive, result]);

  // Verwende kostenlosen Premium-Spin wenn das Spiel beendet ist
  useEffect(() => {
    if (isFreePremiumSpin && !isSpinning && result && !freePremiumSpinUsed) {
      setFreePremiumSpinUsed(true);
      // Callback aufrufen - der Hook wird in der App.tsx aufgerufen
      if (onFreePremiumSpinComplete) {
        onFreePremiumSpinComplete();
      }
    }
  }, [isFreePremiumSpin, isSpinning, result, freePremiumSpinUsed, onFreePremiumSpinComplete]);

  const slotSymbols = ['🍒', '🍋', '🍊', '🍇', '🔔', '💎', '⭐', '🎰'];

  // Verschiedene Begleiter-Namen und Icons nach Seltenheit
  const companionData = {
    common: {
      names: ['Felix', 'Luna', 'Max', 'Bella', 'Charlie', 'Daisy', 'Rocky', 'Mia', 'Buddy', 'Lily'],
      icons: ['🐱', '🐶', '🐹', '🐰', '🐸', '🐤', '🐧', '🦔', '🐨', '🐼']
    },
    rare: {
      names: ['Kristall-Kätzchen', 'Silber-Wolf', 'Gold-Fuchs', 'Saphir-Eule', 'Rubin-Drache', 'Smaragd-Tiger', 'Perlen-Delfin', 'Opal-Schmetterling'],
      icons: ['💎', '🦊', '🦉', '🐅', '🐬', '🦋', '🦄', '🐉']
    },
    epic: {
      names: ['Mystischer Wächter', 'Arkaner Meister', 'Elementar-Geist', 'Himmlischer Bote', 'Magischer Beschützer', 'Uralter Weiser', 'Kosmischer Wanderer'],
      icons: ['🧙‍♂️', '🔮', '⚡', '🌟', '🔥', '❄️', '🌙', '☀️']
    },
    legendary: {
      names: ['Drachen-Kaiser', 'Phoenix-Königin', 'Kristall-Meister', 'Stern-Wächter', 'Mondschein-Sage', 'Sonnen-Herrscher', 'Sturm-Bringer', 'Erd-Titan'],
      icons: ['🐉', '🔥', '⚡', '🌟', '💫', '🌙', '☀️', '❄️']
    }
  };

  const generateCompanion = (type: 'common' | 'rare' | 'epic' | 'legendary', isPremium: boolean = false): Companion => {
    const data = companionData[type];
    const name = data.names[Math.floor(Math.random() * data.names.length)];
    const icon = data.icons[Math.floor(Math.random() * data.icons.length)];

    // Anzahl der Fähigkeiten basierend auf Seltenheit
    const abilityCount = {
      common: 1,
      rare: 2,
      epic: 3,
      legendary: 4
    }[type];

    const abilityTypes = [
      'clickMultiplier', 'coinPerSecond', 'gemChance', 'xpBonus', 
      'gardenBonus', 'autoClickPower', 'upgradeDiscount', 'prestigeBonus'
    ];

    const abilities = [];
    const usedTypes = new Set();

    for (let i = 0; i < abilityCount; i++) {
      let abilityType;
      do {
        abilityType = abilityTypes[Math.floor(Math.random() * abilityTypes.length)];
      } while (usedTypes.has(abilityType));

      usedTypes.add(abilityType);

      // Werte basierend auf Seltenheit
      let value;
      const multiplier = {
        common: 1,
        rare: 2,
        epic: 3,
        legendary: 5
      }[type];

      switch (abilityType) {
        case 'clickMultiplier':
          value = (5 + Math.floor(Math.random() * 15)) * multiplier; // 5-20 * multiplier
          break;
        case 'coinPerSecond':
          value = (3 + Math.floor(Math.random() * 12)) * multiplier; // 3-15 * multiplier
          break;
        case 'gemChance':
          value = (1 + Math.floor(Math.random() * 4)) * multiplier; // 1-5 * multiplier
          break;
        case 'xpBonus':
          value = (10 + Math.floor(Math.random() * 20)) * multiplier; // 10-30 * multiplier
          break;
        case 'gardenBonus':
          value = (15 + Math.floor(Math.random() * 25)) * multiplier; // 15-40 * multiplier
          break;
        case 'autoClickPower':
          value = (2 + Math.floor(Math.random() * 8)) * multiplier; // 2-10 * multiplier
          break;
        case 'upgradeDiscount':
          value = (3 + Math.floor(Math.random() * 7)) * multiplier; // 3-10 * multiplier
          break;
        case 'prestigeBonus':
          value = (8 + Math.floor(Math.random() * 17)) * multiplier; // 8-25 * multiplier
          break;
        default:
          value = 10 * multiplier;
      }

      const descriptions: Record<typeof abilityType, (v: number) => string> = {
        clickMultiplier: (v: number) => `+${v}% Münzen pro Klick`,
        coinPerSecond: (v: number) => `+${v} Münzen pro Sekunde`,
        gemChance: (v: number) => `+${v}% Edelstein-Chance`,
        xpBonus: (v: number) => `+${v}% Erfahrung`,
        gardenBonus: (v: number) => `+${v}% Garten-Boni`,
        autoClickPower: (v: number) => `+${v} Auto-Klick-Power`,
        upgradeDiscount: (v: number) => `${v}% Upgrade-Rabatt`,
        prestigeBonus: (v: number) => `+${v}% Prestige-Punkte`
      };

      abilities.push({
        type: abilityType as any,
        value,
        description: descriptions[abilityType](value)
      });
    }

    const typeDescriptions = {
      common: 'Ein treuer Begleiter',
      rare: 'Ein seltener Begleiter mit besonderen Kräften',
      epic: 'Ein epischer Begleiter mit mächtigen Fähigkeiten',
      legendary: 'Ein legendärer Begleiter mit unglaublicher Macht!'
    };

    return {
      id: `${isPremium ? 'premium_' : 'slot_'}${type}_${name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name,
      type,
      icon,
      description: typeDescriptions[type],
      abilities,
      discoveredAt: new Date().toISOString()
    };
  };

  const spinReels = () => {
    if (isSpinning) return;
    
    // Kostenloser Premium-Spin überspringt die Kosten-Prüfung
    if (!isFreePremiumSpin) {
      if (gems < SPIN_COST) return;
      if (!spendGems(SPIN_COST)) return;
    }

    setIsSpinning(true);
    setResult(null);
    setShowResult(false);

    // Animiere die Rollen für 3 Sekunden
    const spinDuration = 3000;
    const intervalTime = 100;
    let elapsed = 0;

    const spinInterval = setInterval(() => {
      setReels([
        slotSymbols[Math.floor(Math.random() * slotSymbols.length)],
        slotSymbols[Math.floor(Math.random() * slotSymbols.length)],
        slotSymbols[Math.floor(Math.random() * slotSymbols.length)]
      ]);

      elapsed += intervalTime;

      if (elapsed >= spinDuration) {
        clearInterval(spinInterval);
        
        // Bestimme das Ergebnis basierend auf Wahrscheinlichkeiten
        const random = Math.random() * 100; // 0-100%
        
        // Verwende Premium-Chancen wenn kostenloser Premium-Spin
        if (isFreePremiumSpin) {
          if (random < 11) {
            // 11% - Legendärer Begleiter
            setReels(['💎', '💎', '💎']);
            const companion = generateCompanion('legendary');
            setResult({ type: 'legendary', companion });
          } else if (random < 44) {
            // 33% - Epischer Begleiter (11% + 33% = 44%)
            setReels(['⭐', '⭐', '⭐']);
            const companion = generateCompanion('epic');
            setResult({ type: 'epic', companion });
          } else if (random < 64) {
            // 20% - Seltener Begleiter
            setReels(['🔔', '🔔', '🔔']);
            const companion = generateCompanion('rare');
            setResult({ type: 'rare', companion });
          } else if (random < 84) {
            // 20% - Gewöhnlicher Begleiter
            setReels(['🍒', '🍒', '🍒']);
            const companion = generateCompanion('common');
            setResult({ type: 'common', companion });
          } else {
            // 16% - 50% der Diamanten zurück (als Bonus-Edelsteine)
            setReels(['💎', '💎', '🍋']);
            const bonusGems = Math.max(100, Math.floor(gems * 0.05)); // 5% der aktuellen Edelsteine oder mindestens 100
            setResult({ type: 'refund', refundAmount: bonusGems });
          }
        } else {
          // Normale Spin-Chancen
          if (random < 0.1) {
            // 0.1% - Legendärer Begleiter
            setReels(['💎', '💎', '💎']);
            const companion = generateCompanion('legendary');
            setResult({ type: 'legendary', companion });
          } else if (random < 1.1) {
            // 1% - Epischer Begleiter
            setReels(['⭐', '⭐', '⭐']);
            const companion = generateCompanion('epic');
            setResult({ type: 'epic', companion });
          } else if (random < 4.1) {
            // 3% - Seltener Begleiter
            setReels(['🔔', '🔔', '🔔']);
            const companion = generateCompanion('rare');
            setResult({ type: 'rare', companion });
          } else if (random < 14.1) {
            // 10% - Gewöhnlicher Begleiter
            setReels(['🍒', '🍒', '🍒']);
            const companion = generateCompanion('common');
            setResult({ type: 'common', companion });
          } else if (random < 39.1) {
            // 25% - Diamanten zurück
            setReels(['💎', '💎', '🍋']);
            setResult({ type: 'refund', refundAmount: SPIN_COST });
          } else {
            // 60.9% - Nichts gewonnen
            const finalReels = [
              slotSymbols[Math.floor(Math.random() * (slotSymbols.length - 1))],
              slotSymbols[Math.floor(Math.random() * (slotSymbols.length - 1))],
              slotSymbols[Math.floor(Math.random() * (slotSymbols.length - 1))]
            ];
            // Stelle sicher, dass es kein Gewinn ist
            while (finalReels[0] === finalReels[1] && finalReels[1] === finalReels[2]) {
              finalReels[2] = slotSymbols[Math.floor(Math.random() * (slotSymbols.length - 1))];
            }
            setReels(finalReels);
            setResult({ type: 'nothing' });
          }
        }
        
        setIsSpinning(false);
        setTimeout(() => setShowResult(true), 500);
      }
    }, intervalTime);
  };

  const premiumSpin = () => {
    if (gems < PREMIUM_SPIN_COST || isSpinning) return;

    if (!spendGems(PREMIUM_SPIN_COST)) return;

    setIsSpinning(true);
    setResult(null);
    setShowResult(false);

    // Animiere die Rollen für 3 Sekunden
    const spinDuration = 3000;
    const intervalTime = 100;
    let elapsed = 0;

    const spinInterval = setInterval(() => {
      setReels([
        slotSymbols[Math.floor(Math.random() * slotSymbols.length)],
        slotSymbols[Math.floor(Math.random() * slotSymbols.length)],
        slotSymbols[Math.floor(Math.random() * slotSymbols.length)]
      ]);

      elapsed += intervalTime;

      if (elapsed >= spinDuration) {
        clearInterval(spinInterval);
        
        // Premium-Spin Wahrscheinlichkeiten
        const random = Math.random() * 100; // 0-100%
        
        if (random < 11) {
          // 11% - Legendärer Premium-Begleiter (göttlich)
          setReels(['💎', '💎', '💎']);
          const companion = generateCompanion('legendary', true);
          setResult({ type: 'legendary', companion });
        } else if (random < 44) {
          // 33% - Epischer Premium-Begleiter (göttlich)
          setReels(['⭐', '⭐', '⭐']);
          const companion = generateCompanion('epic', true);
          setResult({ type: 'epic', companion });
        } else if (random < 64) {
          // 20% - Seltener Begleiter
          setReels(['🔔', '🔔', '🔔']);
          const companion = generateCompanion('rare');
          setResult({ type: 'rare', companion });
        } else if (random < 84) {
          // 20% - Gewöhnlicher Begleiter
          setReels(['🍒', '🍒', '🍒']);
          const companion = generateCompanion('common');
          setResult({ type: 'common', companion });
        } else if (random < 100) {
          // 16% - 50% der Diamanten zurück
          setReels(['💎', '💎', '🍋']);
          const refundAmount = Math.floor(PREMIUM_SPIN_COST * 0.5);
          setResult({ type: 'refund', refundAmount });
        }
        
        setIsSpinning(false);
        setTimeout(() => setShowResult(true), 500);
      }
    }, intervalTime);
  };

  const handleClaimPrize = () => {
    if (result?.companion) {
      // Füge Begleiter zur Sammlung hinzu
      addCompanionToCollection(result.companion);
      
      // Setze den Begleiter als aktiv (automatisch in einen freien Slot)
      setActiveCompanionOptimized(result.companion);
    } else if (result?.type === 'refund' && result.refundAmount) {
      // Gib Diamanten zurück
      addGems(result.refundAmount);
    }
    
    setResult(null);
    setShowResult(false);
  };

  const startAutoSpin = (type: 'normal' | 'premium', count: number, unlimited: boolean = false) => {
    setAutoSpinType(type);
    setMaxAutoSpins(count);
    setAutoSpinCount(0);
    setIsUnlimitedAutoSpin(unlimited);
    setAutoSpinActive(true);
  };

  const stopAutoSpin = () => {
    setAutoSpinActive(false);
    setAutoSpinCount(0);
    setIsUnlimitedAutoSpin(false);
  };

  const getResultColor = (type: string) => {
    switch (type) {
      case 'legendary': return 'from-yellow-400 to-orange-500';
      case 'epic': return 'from-purple-400 to-pink-500';
      case 'rare': return 'from-blue-400 to-cyan-500';
      case 'common': return 'from-green-400 to-emerald-500';
      case 'refund': return 'from-gray-400 to-gray-600';
      default: return 'from-red-400 to-red-600';
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-xl sm:rounded-2xl p-1 sm:p-2 md:p-6 border border-purple-500/30 shadow-2xl relative overflow-hidden backdrop-blur-sm">
      {/* Modern Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-transparent to-cyan-500" />
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%)`
        }} />
      </div>

      {/* Modern Header */}
      <div className="relative text-center mb-1 sm:mb-2 md:mb-6">
        {isFreePremiumSpin ? (
          <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 backdrop-blur-md rounded-lg sm:rounded-xl p-1 sm:p-2 md:p-4 mb-1 sm:mb-2 md:mb-4 border border-emerald-400/50 shadow-lg">
            <h3 className="text-sm sm:text-base md:text-2xl font-bold text-emerald-300 mb-1">🎰 FREE PREMIUM! 🎰</h3>
            <div className="flex justify-center gap-1 sm:gap-2 text-emerald-200 text-xs sm:text-sm">
              <span className="animate-bounce">💎</span>
              <span className="font-bold">JACKPOT!</span>
              <span className="animate-bounce">💎</span>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-lg sm:rounded-xl p-1 sm:p-2 md:p-4 mb-1 sm:mb-2 md:mb-4 border border-purple-400/50 shadow-lg">
            <h3 className="text-sm sm:text-base md:text-2xl font-bold text-purple-300 mb-1">🎰 JACKPOT PALACE 🎰</h3>
            <div className="flex justify-center gap-1 sm:gap-2 text-purple-200 text-xs sm:text-sm">
              <span className="animate-pulse">✨</span>
              <span className="font-bold">SPIN TO WIN!</span>
              <span className="animate-pulse">✨</span>
            </div>
          </div>
        )}
      </div>

      {/* Modern Gewinnchancen - Collapsible */}
      <div className="relative mb-1 sm:mb-2 md:mb-4">
        <motion.button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full bg-slate-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 sm:p-2 md:p-3 border border-slate-600/50 text-white font-bold text-xs sm:text-sm mb-1 sm:mb-2 hover:bg-slate-700/50 transition-all duration-300"
          whileTap={{ scale: 0.98 }}
        >
          {showDetails ? '▼' : '▶'} Gewinnchancen & Info
        </motion.button>
        
        <AnimatePresence key="companion-slot-machine-details">
          {showDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              {/* Normale Chancen */}
              <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-sm rounded-lg p-2 border border-yellow-500/50">
                  <div className="text-yellow-300 font-bold text-xs">Legendär 🐉</div>
                  <div className="text-white text-xs">0.1%</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-lg p-2 border border-purple-500/50">
                  <div className="text-purple-300 font-bold text-xs">Episch ⭐</div>
                  <div className="text-white text-xs">1%</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-sm rounded-lg p-2 border border-blue-500/50">
                  <div className="text-blue-300 font-bold text-xs">Selten 🔔</div>
                  <div className="text-white text-xs">3%</div>
                </div>
                <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-sm rounded-lg p-2 border border-green-500/50">
                  <div className="text-green-300 font-bold text-xs">Normal 🍒</div>
                  <div className="text-white text-xs">10%</div>
                </div>
              </div>
              
              {/* Premium-Spin Chancen */}
              <div className="bg-gradient-to-r from-yellow-500/30 to-purple-500/30 backdrop-blur-sm rounded-xl p-3 border border-yellow-400/50">
                <div className="text-center mb-2">
                  <div className="text-yellow-300 font-bold text-xs">👑 PREMIUM CHANCEN</div>
                  <div className="text-xs text-yellow-200">Kosten: {formatNumber(PREMIUM_SPIN_COST)} 💎</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-yellow-500/20 backdrop-blur-sm rounded-lg p-2">
                    <div className="text-yellow-300 font-bold text-xs">Legendär: 11%</div>
                  </div>
                  <div className="bg-purple-500/20 backdrop-blur-sm rounded-lg p-2">
                    <div className="text-purple-300 font-bold text-xs">Episch: 33%</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modern Slot Machine */}
      <div className="relative bg-gradient-to-br from-slate-800 via-purple-900 to-slate-800 rounded-lg sm:rounded-2xl p-1 sm:p-2 md:p-6 mb-1 sm:mb-2 md:mb-6 border border-purple-400/50 shadow-2xl backdrop-blur-sm">
        {/* Modern Decorative Lights */}
        <div className="absolute top-2 left-2 right-2 flex justify-between">
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 shadow-lg"
              animate={{
                opacity: [0.3, 1, 0.3],
                scale: [0.8, 1.2, 0.8],
                boxShadow: [
                  '0 0 10px rgba(168, 85, 247, 0.5)',
                  '0 0 20px rgba(168, 85, 247, 0.8)',
                  '0 0 10px rgba(168, 85, 247, 0.5)'
                ]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.3
              }}
            />
          ))}
        </div>

        {/* Main Slot Display - Modern */}
        <div className="bg-gradient-to-br from-slate-900 to-black rounded-lg sm:rounded-2xl p-1 sm:p-2 md:p-6 mb-1 sm:mb-2 md:mb-6 border border-purple-500/50 shadow-inner backdrop-blur-sm">
          <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg sm:rounded-xl p-1 sm:p-2 mb-1 sm:mb-2 md:mb-4 border border-slate-600/50">
            <div className="bg-gradient-to-br from-slate-900 to-black rounded-lg sm:rounded-xl p-1 sm:p-2 md:p-6">
              <div className="grid grid-cols-3 gap-1 sm:gap-2 md:gap-6">
                                  {reels.map((symbol, index) => (
                    <motion.div
                      key={index}
                      className="relative bg-gradient-to-br from-white via-gray-100 to-gray-200 rounded-lg sm:rounded-xl h-12 sm:h-16 md:h-28 flex items-center justify-center text-2xl sm:text-3xl md:text-6xl border border-gray-300/50 shadow-lg backdrop-blur-sm overflow-hidden"
                    animate={isSpinning ? { 
                      scale: [1, 1.02, 1]
                    } : {}}
                    transition={{ 
                      duration: 1.2, 
                      repeat: isSpinning ? Infinity : 0,
                      delay: index * 0.2,
                      ease: "easeInOut"
                    }}
                    style={{
                      boxShadow: isSpinning 
                        ? '0 0 30px rgba(168, 85, 247, 0.8), inset 0 0 20px rgba(255, 255, 255, 0.1)' 
                        : '0 8px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    {isSpinning && (
                      <>
                        {/* Glanz-Effekt */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-40 rounded-xl"
                          animate={{ x: [-200, 200] }}
                          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                        />
                        {/* Farbiger Glanz */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-400 to-transparent opacity-30 rounded-xl"
                          animate={{ x: [-200, 200] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                        />
                        {/* Pulsierender Rand */}
                        <motion.div
                          className="absolute inset-0 border-2 border-purple-400 rounded-xl"
                          animate={{ 
                            opacity: [0.3, 0.8, 0.3],
                            scale: [1, 1.02, 1]
                          }}
                          transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut" }}
                        />
                      </>
                    )}
                    <motion.span 
                      className="relative z-10 drop-shadow-lg"
                      animate={isSpinning ? {
                        rotateY: [0, 180, 360],
                        scale: [1, 1.1, 1]
                      } : {}}
                      transition={{
                        duration: 0.6,
                        repeat: isSpinning ? Infinity : 0,
                        delay: index * 0.1,
                        ease: "easeInOut"
                      }}
                    >
                      {symbol}
                    </motion.span>
                  </motion.div>
                ))}
              </div>
              
              {/* Modern Credits Anzeige */}
              <div className="mt-1 sm:mt-2 md:mt-4 text-center">
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-1 sm:p-2 md:p-3 border border-purple-400/50 inline-block">
                  <div className="text-purple-300 font-bold text-xs sm:text-sm">💎 {formatNumber(gems)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Auto-Spin Status */}
        {autoSpinActive && (
          <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm rounded-lg sm:rounded-xl p-2 sm:p-3 mb-2 sm:mb-3 border border-blue-400/50">
            <div className="text-center text-white text-xs sm:text-sm">
              <div className="font-bold">
                🔄 AUTO-SPIN {isUnlimitedAutoSpin && '∞'}
              </div>
              <div className="text-xs">
                {autoSpinType === 'normal' ? 'Normal' : 'Premium'} {' '}
                {isUnlimitedAutoSpin ? `${autoSpinCount} Spins` : `${autoSpinCount}/${maxAutoSpins}`}
              </div>
              <motion.button
                onClick={stopAutoSpin}
                className="mt-1 sm:mt-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs font-bold transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ⏹ STOPP
              </motion.button>
            </div>
          </div>
        )}

        {/* Modern Spin Buttons */}
        <div className="grid grid-cols-2 gap-1 sm:gap-2 md:gap-4">
          {/* Normal Spin Button */}
          <motion.button
            onClick={spinReels}
            disabled={!isFreePremiumSpin && (gems < SPIN_COST || isSpinning)}
            className={`relative w-full py-2 sm:py-3 md:py-5 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base md:text-xl transition-all duration-300 border-2 ${
              isFreePremiumSpin
                ? 'bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 hover:from-emerald-500/30 hover:to-cyan-500/30 text-emerald-300 shadow-2xl border-emerald-400/50 backdrop-blur-sm'
                : gems >= SPIN_COST && !isSpinning
                ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 text-purple-300 shadow-2xl border-purple-400/50 backdrop-blur-sm'
                : 'bg-gradient-to-r from-slate-600/20 to-slate-700/20 text-slate-400 cursor-not-allowed border-slate-500/50 backdrop-blur-sm'
            }`}
            whileHover={(isFreePremiumSpin || (gems >= SPIN_COST && !isSpinning)) ? { 
              scale: 1.02,
            } : {}}
            whileTap={(isFreePremiumSpin || (gems >= SPIN_COST && !isSpinning)) ? { 
              scale: 0.98,
            } : {}}
          >
            <div className="relative z-10 flex items-center justify-center">
              {isSpinning ? (
                <div className="flex items-center gap-2 sm:gap-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                    className="text-xl sm:text-3xl"
                  >
                    🎰
                  </motion.div>
                  <span className="text-sm sm:text-xl tracking-wider">SPINNING...</span>
                </div>
              ) : isFreePremiumSpin ? (
                <div className="text-center">
                  <div className="text-sm sm:text-2xl mb-1">✨ FREE PREMIUM ✨</div>
                </div>
              ) : gems >= SPIN_COST ? (
                <div className="text-center">
                  <div className="text-xs sm:text-sm md:text-xl mb-1">🎰 SPIN!</div>
                  <div className="text-xs opacity-90">{formatNumber(SPIN_COST)} 💎</div>
                </div>
              ) : (
                <div className="text-center text-slate-400">
                  <div className="text-xs mb-1">💎 NICHT GENUG</div>
                  <div className="text-xs">Brauche: {formatNumber(SPIN_COST)} 💎</div>
                </div>
              )}
            </div>
          </motion.button>

          {/* Premium Spin Button */}
          {!isFreePremiumSpin && (
            <motion.button
              onClick={premiumSpin}
              disabled={gems < PREMIUM_SPIN_COST || isSpinning}
              className={`relative w-full py-2 sm:py-3 md:py-5 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base md:text-2xl transition-all duration-300 border-2 ${
                gems >= PREMIUM_SPIN_COST && !isSpinning
                  ? 'bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 hover:from-yellow-500/30 hover:via-orange-500/30 hover:to-red-500/30 text-yellow-300 shadow-2xl border-yellow-400/50 backdrop-blur-sm'
                  : 'bg-gradient-to-r from-slate-600/20 to-slate-700/20 text-slate-400 cursor-not-allowed border-slate-500/50 backdrop-blur-sm'
              }`}
              whileHover={gems >= PREMIUM_SPIN_COST && !isSpinning ? { 
                scale: 1.02,
              } : {}}
              whileTap={gems >= PREMIUM_SPIN_COST && !isSpinning ? { 
                scale: 0.98,
              } : {}}
            >
              <div className="relative z-10 flex items-center justify-center">
                {isSpinning ? (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <motion.div
                      animate={{ rotate: 360, scale: [1, 1.2, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, ease: "linear" }}
                      className="text-xl sm:text-4xl"
                    >
                      👑
                    </motion.div>
                    <span className="text-sm sm:text-2xl tracking-wider">PREMIUM...</span>
                  </div>
                ) : gems >= PREMIUM_SPIN_COST ? (
                  <div className="text-center">
                    <div className="text-xs sm:text-sm md:text-2xl mb-1">👑 PREMIUM 👑</div>
                    <div className="text-xs mb-1">84% WIN!</div>
                    <div className="text-xs opacity-90">{formatNumber(PREMIUM_SPIN_COST)} 💎</div>
                  </div>
                ) : (
                  <div className="text-center text-slate-400">
                    <div className="text-xs mb-1">👑 GESPERRT</div>
                    <div className="text-xs">Brauche: {formatNumber(PREMIUM_SPIN_COST)} 💎</div>
                  </div>
                )}
              </div>
            </motion.button>
          )}
        </div>

        {/* Modern Auto-Spin Buttons */}
        {!isFreePremiumSpin && !autoSpinActive && (
          <div className="mt-1 sm:mt-2 md:mt-4 p-1 sm:p-2 md:p-4 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur-sm rounded-lg sm:rounded-xl border border-indigo-400/30">
            <h4 className="text-white font-bold text-center mb-1 sm:mb-2 md:mb-3 text-xs sm:text-sm md:text-base">🔄 AUTO-SPIN</h4>
            
            {/* Normal Auto-Spin - Modern */}
            <div className="mb-1 sm:mb-2 md:mb-3">
              <div className="text-white text-xs mb-1">Normal:</div>
              <div className="grid grid-cols-4 gap-1 sm:gap-2">
                {[
                  { count: 10, cost: SPIN_COST * 10 },
                  { count: 25, cost: SPIN_COST * 25 },
                  { count: 50, cost: SPIN_COST * 50 }
                ].map(({ count, cost }) => (
                  <motion.button
                    key={count}
                    onClick={() => startAutoSpin('normal', count)}
                    disabled={gems < cost}
                    className={`py-2 px-3 rounded-lg text-xs font-bold transition-all duration-300 ${
                      gems >= cost
                        ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30 text-white border border-blue-400/50 backdrop-blur-sm'
                        : 'bg-gradient-to-r from-slate-600/20 to-slate-700/20 text-slate-400 cursor-not-allowed border border-slate-500/50 backdrop-blur-sm'
                    }`}
                    whileHover={gems >= cost ? { scale: 1.05 } : {}}
                    whileTap={gems >= cost ? { scale: 0.95 } : {}}
                  >
                    {count}x
                    <div className="text-xs">{formatNumber(cost)} 💎</div>
                  </motion.button>
                ))}
                {/* Unbegrenzter Normal Auto-Spin */}
                <motion.button
                  onClick={() => startAutoSpin('normal', 0, true)}
                  disabled={gems < SPIN_COST}
                  className={`py-2 px-3 rounded-lg text-xs font-bold transition-all duration-300 ${
                    gems >= SPIN_COST
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30 text-white border border-blue-400/50 backdrop-blur-sm'
                      : 'bg-gradient-to-r from-slate-600/20 to-slate-700/20 text-slate-400 cursor-not-allowed border border-slate-500/50 backdrop-blur-sm'
                  }`}
                  whileHover={gems >= SPIN_COST ? { scale: 1.05 } : {}}
                  whileTap={gems >= SPIN_COST ? { scale: 0.95 } : {}}
                >
                  ∞
                  <div className="text-xs">∞</div>
                </motion.button>
              </div>
            </div>

            {/* Premium Auto-Spin - Modern */}
            <div>
              <div className="text-yellow-300 text-xs mb-1">👑 Premium:</div>
              <div className="grid grid-cols-3 gap-1 sm:gap-2">
                {[
                  { count: 5, cost: PREMIUM_SPIN_COST * 5 },
                  { count: 10, cost: PREMIUM_SPIN_COST * 10 }
                ].map(({ count, cost }) => (
                  <motion.button
                    key={count}
                    onClick={() => startAutoSpin('premium', count)}
                    disabled={gems < cost}
                    className={`py-2 px-3 rounded-lg text-xs font-bold transition-all duration-300 ${
                      gems >= cost
                        ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-white border border-yellow-400/50 backdrop-blur-sm'
                        : 'bg-gradient-to-r from-slate-600/20 to-slate-700/20 text-slate-400 cursor-not-allowed border border-slate-500/50 backdrop-blur-sm'
                    }`}
                    whileHover={gems >= cost ? { scale: 1.05 } : {}}
                    whileTap={gems >= cost ? { scale: 0.95 } : {}}
                  >
                    {count}x
                    <div className="text-xs">{formatNumber(cost)} 💎</div>
                  </motion.button>
                ))}
                {/* Unbegrenzter Premium Auto-Spin */}
                <motion.button
                  onClick={() => startAutoSpin('premium', 0, true)}
                  disabled={gems < PREMIUM_SPIN_COST}
                  className={`py-2 px-3 rounded-lg text-xs font-bold transition-all duration-300 ${
                    gems >= PREMIUM_SPIN_COST
                      ? 'bg-gradient-to-r from-yellow-500/20 to-red-500/20 hover:from-yellow-500/30 hover:to-red-500/30 text-white border border-yellow-400/50 backdrop-blur-sm'
                      : 'bg-gradient-to-r from-slate-600/20 to-slate-700/20 text-slate-400 cursor-not-allowed border border-slate-500/50 backdrop-blur-sm'
                  }`}
                  whileHover={gems >= PREMIUM_SPIN_COST ? { scale: 1.05 } : {}}
                  whileTap={gems >= PREMIUM_SPIN_COST ? { scale: 0.95 } : {}}
                >
                  ∞
                  <div className="text-xs">∞</div>
                </motion.button>
              </div>
            </div>
          </div>
        )}
      </div>



      {/* Modern Ergebnis Modal */}
      <AnimatePresence key="companion-slot-machine-result-modal">
        {showResult && result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className={`bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-8 rounded-2xl max-w-sm sm:max-w-md w-full max-h-[90vh] overflow-y-auto border border-purple-400/50 ${
                autoSpinActive ? 'border-2 border-blue-400/50' : ''
              }`}
            >
              {/* Auto-Spin Hinweis */}
              {autoSpinActive && (
                <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl p-3 mb-4 text-center border border-blue-400/50">
                  <div className="text-white text-xs font-bold">
                    🔄 AUTO-SPIN AKTIV - Läuft automatisch weiter...
                  </div>
                </div>
              )}
              
              {result.companion ? (
                <div className="text-center">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.3, 1],
                      rotate: [0, 10, -10, 0]
                    }}
                    transition={{ duration: 0.6, repeat: 3 }}
                    className="text-4xl sm:text-8xl mb-3 sm:mb-4"
                  >
                    🎉
                  </motion.div>
                  <h2 className="text-2xl sm:text-4xl font-bold text-yellow-300 mb-2 sm:mb-4">
                    {result.type === 'legendary' ? 'JACKPOT!' : 
                     result.type === 'epic' ? 'EPISCH!' :
                     result.type === 'rare' ? 'SELTEN!' : 'GEWONNEN!'}
                  </h2>
                  <p className="text-white text-lg sm:text-xl mb-3 sm:mb-6">
                    Du hast {result.companion.name} gewonnen!
                  </p>
                  <div className={`bg-gradient-to-br ${getResultColor(result.type)} p-4 sm:p-6 rounded-xl border-2 shadow-lg mb-3 sm:mb-6`}>
                    <div className="text-4xl sm:text-6xl mb-2 sm:mb-3">{result.companion.icon}</div>
                    <h3 className="text-lg sm:text-2xl font-bold text-white mb-2 sm:mb-3">{result.companion.name}</h3>
                    <div className="text-xs sm:text-sm text-white/90 mb-2 sm:mb-3 capitalize">{result.companion.type} Begleiter</div>
                    <div className="space-y-2 sm:space-y-3 max-h-32 sm:max-h-none overflow-y-auto">
                      {result.companion.abilities.map((ability, idx) => (
                        <div key={idx} className="text-white bg-black/20 rounded-lg px-3 py-2 text-xs sm:text-sm border border-white/10">
                          {ability.description}
                        </div>
                      ))}
                    </div>
                  </div>
                  {!autoSpinActive && (
                    <div className="space-y-3 sm:space-y-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleClaimPrize}
                        className="w-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl text-sm sm:text-base border border-green-400/50 transition-all duration-300"
                      >
                        Begleiter aktivieren!
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          // Begleiter zur Sammlung hinzufügen ohne zu aktivieren
                          if (result.companion) {
                            addCompanionToCollection(result.companion);
                          }
                          setResult(null);
                          setShowResult(false);
                        }}
                        className="w-full bg-gradient-to-r from-slate-600/20 to-slate-700/20 hover:from-slate-600/30 hover:to-slate-700/30 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl text-sm sm:text-base border border-slate-500/50 transition-all duration-300"
                      >
                        Zur Sammlung hinzufügen
                      </motion.button>
                    </div>
                  )}
                </div>
              ) : result.type === 'refund' ? (
                <div className="text-center">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.5, repeat: 2 }}
                    className="text-4xl sm:text-8xl mb-4 sm:mb-6"
                  >
                    💎
                  </motion.div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-blue-300 mb-2 sm:mb-4">
                    Einsatz zurück!
                  </h2>
                  <p className="text-white text-base sm:text-lg mb-4 sm:mb-6">
                    Du erhältst {formatNumber(result.refundAmount || 0)} 💎 zurück!
                  </p>
                  {!autoSpinActive && (
                    <div className="space-y-3 sm:space-y-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleClaimPrize}
                        className="w-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 hover:from-blue-500/30 hover:to-cyan-500/30 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl text-sm sm:text-base border border-blue-400/50 transition-all duration-300"
                      >
                        Diamanten einsammeln
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          // Diamanten zurückgeben und Modal schließen
                          if (result.refundAmount) {
                            addGems(result.refundAmount);
                          }
                          setResult(null);
                          setShowResult(false);
                        }}
                        className="w-full bg-gradient-to-r from-slate-600/20 to-slate-700/20 hover:from-slate-600/30 hover:to-slate-700/30 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl text-sm sm:text-base border border-slate-500/50 transition-all duration-300"
                      >
                        Schließen
                      </motion.button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 0.5, repeat: 1 }}
                    className="text-4xl sm:text-8xl mb-4 sm:mb-6"
                  >
                    😢
                  </motion.div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-red-300 mb-2 sm:mb-4">
                    Kein Gewinn
                  </h2>
                  <p className="text-white text-base sm:text-lg mb-4 sm:mb-6">
                    Diesmal hat es nicht geklappt. Versuche es nochmal!
                  </p>
                  {!autoSpinActive && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowResult(false)}
                      className="w-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 hover:from-blue-500/30 hover:to-cyan-500/30 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl text-sm sm:text-base border border-blue-400/50 transition-all duration-300"
                    >
                      Nochmal versuchen
                    </motion.button>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanionSlotMachine; 